// Shared activity-feed rendering: the rich per-day breakdown (active users + their events,
// anonymous visitors + their events, new projects, changed projects + audited diffs) used by the
// admin Activity drill-down page, plus the small presentational primitives the admin page also
// reuses (ExpandableContainer, timeAgo, formatTimestamp, UserLink). Styling lives in
// src/pages/Settings.css (imported by the consuming pages).
import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Link } from './nav'
import { ChevronRight, ChevronDown, User, LogIn, UserPlus, FolderPlus, Pencil, Eye, MousePointerClick, Keyboard, Activity, Bug, FlaskConical, AlertTriangle, CheckCircle2, Clapperboard, Mail, MailCheck, Star, Circle, History, Target, Sparkles } from 'lucide-react'
import Spinner from './Spinner'
import { apiFetch } from '../lib/apiFetch'
import { eventDescription } from '../lib/eventDescriptions'
import { useSendCompletionEmailMutation } from '../services/projectDataApi'

export interface AnalyticsEventEntry {
  id: string
  eventType: number
  eventName: string | null
  urlPath: string | null
  pageTitle: string | null
  tag: string | null
  browser: string | null
  os: string | null
  device: string | null
  eventData: Record<string, unknown> | null
  createdAt: string
  // When set, overrides the actor's anonymity for this one event — used on the user detail page to mark
  // events recorded before the user signed in (attributed via the persistent browser session_id).
  anonymous?: boolean
}

export interface BasicUser {
  userId: string
  email: string | null
  name: string | null
}

export interface ActiveUser extends BasicUser {
  sessionCount: number
  eventCount: number
  pageviewCount: number
  // When the user first joined, and the most recent time they were seen (independent of the day
  // being viewed) — both ISO strings, used to show "joined Nd ago" / "last seen Nd ago".
  signupAt: string | null
  lastSeenAt: string | null
  // Admin-only LLM-generated summary of this user's activity on this day, or null when none has been
  // generated yet (see user_activity_summaries).
  summary?: string | null
  events: AnalyticsEventEntry[]
}

export interface AnonVisitor {
  distinctId: string
  ip: string | null
  browser: string | null
  os: string | null
  sessionCount: number
  eventCount: number
  pageviewCount: number
  // When this anonymous visitor's browser was later seen signed in (matched by the persistent
  // analytics session_id), the account it belongs to — else null. Read-time identity stitch.
  linkedUserId?: string | null
  linkedUserLabel?: string | null
  events: AnalyticsEventEntry[]
}

export interface ProjectFieldChange {
  field: string
  old: string | null
  new: string | null
}

export interface ProjectChange {
  performedAt: string
  changedFields: string[]
  fields: ProjectFieldChange[]
}

export interface DailyProjectEntry {
  id: string
  name: string
  ownerId: string | null
  ownerEmail: string | null
  createdAt?: string
  updatedAt?: string
  bugCount?: number
  testRunCount?: number
  inProgress?: boolean
  finishedAt?: string | null
  // Seconds from project creation to when the first rrweb session data was received, or null if none yet.
  firstRrwebSeconds?: number | null
  // When a completion email was last successfully sent for this project, or null if none yet.
  completionEmailSentAt?: string | null
}

export interface ChangedProjectEntry extends DailyProjectEntry {
  changes: ProjectChange[]
}

// Acquisition pageviews for a day attributed to one referrer + campaign source — homepage landings
// plus any utm_*-tagged landing on a deeper path (share links, completion-email). `referrer` is the
// referrer domain, or null for direct / no-referrer visits. The `utm*` fields are the campaign
// parameters parsed from the visited URL (null when absent).
export interface VisitReferrer {
  referrer: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
  count: number
}

// The light per-occurrence shape the Errors feed carries — just enough to group by tool/message and
// list occurrences. The full stored record (params, recording, container, timing) is intentionally
// not sent here; it loads on demand from /api/admin-task-error when an occurrence's detail page opens.
// Error vs warning: a warning is a less-severe, typically recovered-from event (see the server
// classifier in netlify/functions/lib/error-severity.ts). Mirrors the server-supplied `severity`.
export type ErrorSeverity = 'error' | 'warning'

export interface TaskErrorDetail {
  id: string
  // Where the error came from: 'mcp' (Replay MCP call failure) or any other producer's tag.
  source: string
  kind: string | null
  tool: string | null
  message: string | null
  severity: ErrorSeverity
  occurredAt: string
  recordingId: string | null
  // The error-processing bucket (name) this error was sorted into, or null while still unprocessed.
  bucket?: string | null
  // Opt-in: include=stack / include=baggage (include_stack=1 still enables stack).
  stack?: string | null
  baggage?: unknown
}

export interface ErrorTask {
  id: string
  goal: string | null
  status: string
  // A task is "failed" when its own status is failed; otherwise it passed but still hit Replay MCP
  // backend errors during its run. The UI uses this to distinguish failed from passed tasks.
  failed: boolean
  lastActivityAt: string
  errors: TaskErrorDetail[]
}

// A container that failed to spawn (or timed out) for a project — recorded in app_container_events
// with action 'spawn-failed'. Warm-pool (non-project) failures arrive grouped under a synthetic
// "Warm pool" project from the server.
export interface SpawnFailure {
  id: string
  name: string | null
  machineId: string | null
  role: string | null
  reason: string | null
  occurredAt: string
  bucket?: string | null
}

// A client-side error event (analytics_events.is_error) — e.g. the chunk-load failure behind the
// "Replay unavailable" fallback. Grouped under a synthetic "Client errors" project in the errors feed.
export interface ClientError {
  id: string
  name: string | null
  tag: string | null
  urlPath: string | null
  userId: string | null
  message: string | null
  severity: ErrorSeverity
  occurredAt: string
  bucket?: string | null
}

// A backend request that returned a server error (backend_requests.http_status_code >= 500),
// materialized as an error record by the cron-backend-errors scheduled function. Grouped under a
// synthetic "Backend errors" project in the errors feed.
export interface BackendRequestError {
  id: string
  handlerPath: string | null
  httpStatusCode: number | null
  message: string | null
  // Machine-readable category (e.g. 'llm-fallback' | 'llm-failure' | 'llm-slow'), or null for a
  // generic 5xx. Used as the row's type label; the full detail/metadata load from the detail page.
  errorSource: string | null
  recordingId: string | null
  severity: ErrorSeverity
  occurredAt: string
  bucket?: string | null
}

export interface ProjectErrors {
  projectId: string
  projectName: string
  ownerId: string | null
  ownerEmail: string | null
  failedTaskCount: number
  mcpErrorTaskCount: number
  spawnFailureCount: number
  // Count of client-side error events (only set on the synthetic client-errors project).
  clientErrorCount: number
  // Count of backend server-error records (only set on the synthetic backend-errors project).
  backendErrorCount: number
  tasks: ErrorTask[]
  spawnFailures: SpawnFailure[]
  // Client-side error events (newest-first), only on the synthetic client-errors project.
  clientErrors: ClientError[]
  // Backend server-error records (newest-first), only on the synthetic backend-errors project.
  backendErrors: BackendRequestError[]
}

// Per-day counts for every section — what the detail page loads first (one cheap request) to render
// the day list + totals. Each section's actual rows are fetched lazily from its own endpoint when
// the user expands that group, so no single request pulls all the events/errors in the range.
export interface ActivityDaySummary {
  date: string
  signups: number
  activeUsers: number
  anonVisitors: number
  newProjects: number
  changedProjects: number
  bugsFound: number
  visits: number
  events: number
  errors: number
}

// One analytics event within a kind group: when it happened and who triggered it (a logged-in user
// or an anonymous IP), plus light context (path / tag).
export interface EventOccurrence {
  id: string
  createdAt: string
  userId: string | null
  label: string
  anonymous: boolean
  ip: string | null
  urlPath: string | null
  tag: string | null
}

// A day's analytics events for one activity bucket — a named page-route group (e.g. "Project bugs")
// for pageviews, or the targeted element/feature for custom events. `kind` is the bucket label.
// `count` is the true total even when `occurrences` is truncated server-side. `key` is a stable
// unique id (two buckets may share a display `kind`, e.g. the same target clicked and typed into).
export interface EventKind {
  key: string
  kind: string
  eventType: number
  eventName: string | null
  count: number
  userCount: number
  // Admin-set importance level: explicit 0–3 (higher sorts first), or absent/IMPORTANCE_UNSET for "not
  // set". Set buckets sort to the top of every event display and are marked (a dot at 0, one star per
  // level 1–3). The server sets this on the daily/session feeds; client-grouped views (a single
  // visitor's events) learn it from the importance-levels hook instead.
  important?: number
  occurrences: EventOccurrence[]
}

// One acquisition UTM "source" value's performance over a period: landing pageviews carrying it
// (Visits) vs. accounts whose first-touch source it was (Signups). Mirrors the server's UtmSourceStat
// and the "Top sources" table on the main admin page.
export interface UtmSourceStat {
  source: string
  visits: number
  signups: number
}

// The payload each lazy section endpoint returns for a single day.
interface SignupsDayData { date: string; signups: BasicUser[] }
interface ActiveUsersDayData { date: string; users: ActiveUser[] }
interface AnonymousDayData { date: string; visitors: AnonVisitor[] }
interface NewProjectsDayData { date: string; newProjects: DailyProjectEntry[] }
interface ChangedProjectsDayData { date: string; changedProjects: ChangedProjectEntry[] }
interface VisitsDayData { date: string; visits: VisitReferrer[] }
interface SourcesDayData { date: string; sources: UtmSourceStat[] }
interface EventsDayData { date: string; kinds: EventKind[] }

export function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 0) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

// Absolute local timestamp for event feeds (analytics events, MCP errors, container events), where a
// precise time is more useful than a relative "44m ago". Includes the date since these feeds span
// multiple days; seconds are included because events often land within the same minute.
export function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

// Formats a UTC day key (YYYY-MM-DD) for display. Day buckets are computed in UTC on the
// server, so "Today"/"Yesterday" are also resolved against the UTC calendar.
export function formatActivityDay(dateStr: string): string {
  const label = new Date(`${dateStr}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
  const todayUtc = new Date().toISOString().slice(0, 10)
  const yesterdayUtc = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)
  if (dateStr === todayUtc) return `Today — ${label}`
  if (dateStr === yesterdayUtc) return `Yesterday — ${label}`
  return label
}

export function userLabel(u: BasicUser): string {
  return u.email ?? u.name ?? u.userId
}

// Admin-only per-user history page. User ids contain provider-prefixed characters (e.g. `auth0|…`,
// `email:…`) so they're URL-encoded into the path.
export function userLink(userId: string): string {
  return `/admin/users/${encodeURIComponent(userId)}`
}

// Admin-only detail page for a single MCP error, keyed on its id. Admin error surfaces link each
// error here (rather than expanding inline) so every field — params, recording, timing — lives on
// one shareable page.
export function adminErrorUrl(errorId: string): string {
  return `/admin/errors/${encodeURIComponent(errorId)}`
}

// The admin detail page for one analytics activity bucket ("event kind"). The bucket key can contain
// any character (slashes, colons), so it travels as a query param rather than a path segment.
export function eventDetailUrl(key: string): string {
  return `/admin/events?key=${encodeURIComponent(key)}`
}

export const IMPORTANCE_UNSET = -1
// The values the picker offers, most-important first, ending with the explicit 0 and then "not set".
export const IMPORTANCE_CHOICES = [3, 2, 1, 0, IMPORTANCE_UNSET]

// Persist a bucket's importance level (0–3, or IMPORTANCE_UNSET to clear) and invalidate the shared
// levels cache so every event display re-marks/re-sorts. Returns the level the server stored, or null
// on failure. Used by the inline picker on event rows and the event detail page.
export async function setEventImportanceLevel(eventKey: string, level: number): Promise<number | null> {
  try {
    const res = await apiFetch('/api/admin-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: eventKey, important: level }),
    })
    if (!res.ok) return null
    const payload = await res.json().catch(() => ({})) as { important?: number }
    refreshImportantEventLevels()
    return typeof payload.important === 'number' ? payload.important : level
  } catch {
    return null
  }
}

// Module-level cache of the importance-levels fetch so the many event rows across a page share one
// request. Reset via refreshImportantEventLevels() after a level is changed.
let importantLevelsPromise: Promise<Map<string, number>> | null = null
const importantLevelsSubscribers = new Set<() => void>()

async function fetchImportantEventLevels(): Promise<Map<string, number>> {
  try {
    const res = await apiFetch('/api/admin-event')
    if (!res.ok) return new Map()
    const data = await res.json() as { levels?: Record<string, number> }
    return new Map(Object.entries(data.levels ?? {}))
  } catch {
    return new Map()
  }
}

// Invalidate the shared cache and notify every mounted hook to refetch — called after an admin changes
// a bucket's importance level so all event displays re-star/re-sort without a full reload.
export function refreshImportantEventLevels(): void {
  importantLevelsPromise = null
  for (const cb of importantLevelsSubscribers) cb()
}

// Explicit importance level (0–3) of every flagged bucket, keyed by bucket key, shared across all event
// displays so they can mark and sort important events first. A bucket absent from the map is "not set".
// Best-effort: an empty map while loading or on error.
export function useImportantEventLevels(): Map<string, number> {
  const [levels, setLevels] = useState<Map<string, number>>(new Map())
  useEffect(() => {
    let active = true
    const load = () => {
      if (!importantLevelsPromise) importantLevelsPromise = fetchImportantEventLevels()
      importantLevelsPromise.then(l => { if (active) setLevels(l) })
    }
    importantLevelsSubscribers.add(load)
    load()
    return () => { active = false; importantLevelsSubscribers.delete(load) }
  }, [])
  return levels
}

// A clickable user label that links to their admin history page.
export function UserLink({ userId, label }: { userId: string; label: string }) {
  return (
    <Link to={userLink(userId)} className="settings-admin__user-link" data-analytics-event="admin-user-history-link">
      <User size={12} aria-hidden />
      {label}
    </Link>
  )
}

// Bug + test-run counts for a project, rendered the same way across the admin pages (the per-user
// project list and the activity drill-down's new/changed projects).
export function ProjectStats({ bugCount, testRunCount }: { bugCount: number; testRunCount: number }) {
  return (
    <span className="settings-admin__user-project-stats">
      <Bug size={12} aria-hidden /> {bugCount}
      <FlaskConical size={12} aria-hidden style={{ marginLeft: 8 }} /> {testRunCount}
    </span>
  )
}

// Time a project's QA took, in minutes, shown across the admin project lists. For a finished
// project that's (latest completion − created); while it still has outstanding work it's the
// elapsed time so far. A spinning loader marks in-progress projects, a green check marks finished
// ones. Renders nothing when there's no usable timing yet (finished but nothing ever completed).
export function ProjectDuration({ createdAt, finishedAt, inProgress }: {
  createdAt: string
  finishedAt: string | null
  inProgress: boolean
}) {
  const startMs = new Date(createdAt).getTime()
  const endMs = inProgress ? Date.now() : (finishedAt ? new Date(finishedAt).getTime() : null)
  if (endMs == null || Number.isNaN(startMs)) return null
  const minutes = Math.max(0, Math.round((endMs - startMs) / 60000))
  return (
    <span
      className="settings-admin__user-project-duration"
      title={inProgress ? 'In progress — elapsed time so far' : 'Finished — QA duration from project creation'}
    >
      {inProgress
        ? <Spinner size={12} />
        : <CheckCircle2 size={12} aria-hidden className="settings-admin__duration-done" />}
      {minutes}m
    </span>
  )
}

// Humanizes the seconds-to-first-rrweb delay: bare seconds when short, minutes/hours when longer.
// The exact second count always lives in the cell's title so the precise value is never lost.
function formatRrwebDelay(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 120) return `${s}s`
  const minutes = Math.round(s / 60)
  if (minutes < 120) return `${minutes}m`
  return `${Math.round(minutes / 60)}h`
}

// Time from a project's creation to when the first rrweb session data was received, shown as a column
// in the activity new-projects list. Always renders (an em dash when no rrweb exists yet) so the
// surrounding grid columns stay aligned across rows.
export function ProjectFirstRrweb({ seconds }: { seconds: number | null }) {
  return (
    <span
      className="settings-admin__user-project-rrweb"
      title={seconds == null
        ? 'No rrweb data has been received yet'
        : `${Math.max(0, Math.round(seconds))}s from project creation to the first rrweb data received`}
    >
      <Clapperboard size={12} aria-hidden />
      {seconds == null ? '—' : formatRrwebDelay(seconds)}
    </span>
  )
}

// Completion-email indicator shown next to a project in the admin Activity listings. When an email
// has been sent it's a static green "mail-check" with the time in its tooltip; when none has been
// sent it's a clickable mail button that sends the completion email to the project owner on demand
// (PATCH /api/projects/:id, action send-completion-email, recipient: owner). After a successful
// send (or a queue, when QA is still running) it flips to the sent state in place.
export function CompletionEmailIcon({ projectId, sentAt }: { projectId: string; sentAt: string | null }) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>(sentAt ? 'sent' : 'idle')
  const [sendCompletionEmail] = useSendCompletionEmailMutation()
  const [sentLabel, setSentLabel] = useState<string | null>(sentAt)
  const [error, setError] = useState<string | null>(null)

  if (status === 'sent') {
    return (
      <span
        className="settings-admin__completion-email settings-admin__completion-email--sent"
        title={sentLabel ? `Completion email sent ${timeAgo(sentLabel)}` : 'Completion email sent'}
        data-testid="completion-email-sent"
      >
        <MailCheck size={12} aria-hidden />
      </span>
    )
  }

  async function handleSend(e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    setStatus('sending')
    setError(null)
    try {
      const data = await sendCompletionEmail({ projectId, recipient: 'owner' }).unwrap()
      if (data.success) {
        setSentLabel(new Date().toISOString())
        setStatus('sent')
      } else {
        setError(data.error ?? 'Failed to send the completion email.')
        setStatus('error')
      }
    } catch {
      setError('Could not reach the server.')
      setStatus('error')
    }
  }

  const title =
    status === 'error'
      ? `${error ?? 'Send failed'} — click to retry`
      : 'Completion email not sent — click to send to the project owner'

  return (
    <button
      type="button"
      className={`settings-admin__completion-email settings-admin__completion-email--send${status === 'error' ? ' settings-admin__completion-email--error' : ''}`}
      onClick={handleSend}
      disabled={status === 'sending'}
      title={title}
      aria-label={title}
      data-testid="completion-email-send"
      data-analytics-event="admin-activity-send-completion-email"
    >
      {status === 'sending'
        ? <Spinner size={12} />
        : <Mail size={12} aria-hidden />}
    </button>
  )
}

function groupProjectsByOwner(projects: DailyProjectEntry[]): [string, DailyProjectEntry[]][] {
  const map = new Map<string, DailyProjectEntry[]>()
  for (const p of projects) {
    const key = p.ownerEmail ?? p.ownerId ?? 'unknown'
    const arr = map.get(key) ?? []
    arr.push(p)
    map.set(key, arr)
  }
  return [...map.entries()]
}

export function ExpandableContainer({ title, subtitle, icon, actions, children }: {
  title: string
  subtitle?: string
  icon?: ReactNode
  // Optional controls (e.g. a link) shown on the right of the header, outside the toggle button so a
  // link isn't illegally nested inside a <button>. When present the header uses the split layout.
  actions?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const header = (
    <button type="button" className={actions ? 'settings-admin__expandable-toggle' : 'settings-admin__expandable-header'} onClick={() => setOpen(!open)} data-analytics-event="admin-expandable-toggle">
      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      {icon}
      <span className="settings-admin__expandable-title">{title}</span>
      {subtitle && <span className="settings-admin__expandable-sub">{subtitle}</span>}
    </button>
  )
  return (
    <div className="settings-admin__expandable">
      {actions ? (
        <div className="settings-admin__expandable-header settings-admin__expandable-header--split">
          {header}
          <span className="settings-admin__expandable-actions">{actions}</span>
        </div>
      ) : header}
      {open && <div className="settings-admin__expandable-body">{children}</div>}
    </div>
  )
}

// The lazy section endpoints, keyed by the path segment after /api/admin-activity-.
type ActivitySection = 'signups' | 'active-users' | 'anonymous' | 'new-projects' | 'changed-projects' | 'visits' | 'sources' | 'events' | 'errors'

// Fetch one section's rows for a single day from its own endpoint, returning just that day's entry.
// When `hour` (0–23 UTC) is set, the endpoint narrows the day to that hour (the hourly drill-down).
// Fetch one section's rows over [start, end]. With `aggregate`, the endpoint collapses the whole
// range into one combined bucket (the week/period view) and we take that single entry; otherwise it
// returns one entry per day and we pick the requested day (start === end for the single-day view).
async function fetchSectionRange<T extends { date: string }>(section: ActivitySection, start: string, end: string, hour: number | null, externalOnly = false, aggregate = false): Promise<T | null> {
  const hourQs = hour != null ? `&hour=${hour}` : ''
  const extQs = externalOnly ? '&external_only=1' : ''
  const aggQs = aggregate ? '&aggregate=1' : ''
  const res = await apiFetch(`/api/admin-activity-${section}?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${hourQs}${extQs}${aggQs}`)
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error ?? `HTTP ${res.status}`)
  }
  const data = await res.json() as { days?: T[] }
  return aggregate ? (data.days?.[0] ?? null) : (data.days?.find(d => d.date === start) ?? null)
}

// A collapsible section group inside a day. The header always shows the section's count (from the
// summary), so nothing is fetched until the user opens it; on first open it lazily loads that day's
// rows from the section endpoint and renders them. This is what keeps the detail page off the old
// all-in-one query — each section is its own request, made only on demand.
function LazyActivitySection<T extends { date: string }>({ icon, title, count, start, end, aggregate, hour, externalOnly, section, render }: {
  icon: ReactNode
  title: string
  count: number
  start: string
  end: string
  aggregate?: boolean
  hour?: number | null
  externalOnly?: boolean
  section: ActivitySection
  render: (data: T) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<T | null>(null)
  const [loaded, setLoaded] = useState(false)
  const openRef = useRef(false)
  // count gates whether there's anything to fetch; read via a ref so it doesn't re-key `load` (and
  // thus the invalidation effect), which would double-fetch when the summary count updates too.
  const countRef = useRef(count)
  countRef.current = count

  const load = useCallback(async () => {
    if (countRef.current === 0) return
    setLoading(true)
    setError(null)
    try {
      setData(await fetchSectionRange<T>(section, start, end, hour ?? null, externalOnly, aggregate))
      setLoaded(true)
    } catch (e) {
      if ((e as Error).name !== 'AuthError') setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [section, start, end, aggregate, hour, externalOnly])

  const toggle = () => {
    const next = !open
    setOpen(next)
    openRef.current = next
    if (next && !loaded && !loading) void load()
  }

  // When the query scope changes (the External-only toggle, or the hour/date/section), the cached
  // rows are stale: drop them so a collapsed section refetches fresh on next open, and refetch
  // immediately if it's currently open. Without this, toggling External only refreshed only the
  // summary counts while expanded sections kept showing the previous filter's rows.
  useEffect(() => {
    setLoaded(false)
    setData(null)
    setError(null)
    if (openRef.current) void load()
  }, [load])

  return (
    <div className="settings-admin__day-group">
      <button
        type="button"
        className="settings-admin__day-group-title settings-admin__day-group-toggle"
        onClick={toggle}
        disabled={count === 0}
        data-testid={`activity-section-${section}`}
        data-analytics-event={`admin-activity-section-${section}-toggle`}
      >
        {count > 0
          ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)
          : <span className="settings-admin__day-group-spacer" aria-hidden />}
        {icon}
        {title} ({count})
      </button>
      {open && count > 0 && (
        loading
          ? <div className="settings-admin__day-empty">Loading…</div>
          : error
            ? <div className="settings-admin__day-empty">Failed to load: {error}</div>
            : data
              ? render(data)
              : <div className="settings-admin__day-empty">None</div>
      )}
    </div>
  )
}

// Client-side event bucketing — mirrors the server's getEventsByDay grouping (lib/activity.ts) so a
// single visitor's events expand into the same activity buckets the day-level Events section shows:
// pageviews grouped by named route, custom events grouped by the targeted element/feature.
const PAGE_ROUTE_GROUPS: [RegExp, string | ((m: RegExpMatchArray) => string)][] = [
  [/^\/$/, 'Home'],
  [/^\/new$/, 'New project'],
  [/^\/projects$/, 'Projects list'],
  [/^\/projects\/[^/]+\/bugs\/[^/]+$/, 'Bug detail'],
  [/^\/projects\/[^/]+\/tasks\/[^/]+$/, 'Task detail'],
  [/^\/projects\/[^/]+\/journeys\/[^/]+$/, 'Journey detail'],
  [/^\/projects\/[^/]+\/processing-history$/, 'Processing history'],
  [/^\/projects\/[^/]+\/([^/]+)$/, m => `Project ${m[1]}`],
  [/^\/projects\/[^/]+$/, 'Project'],
  [/^\/guidance\/updates\/[^/]+$/, 'Guidance update'],
  [/^\/guidance$/, 'Guidance'],
  [/^\/insights$/, 'Insights'],
  [/^\/admin\/activity$/, 'Admin · Activity'],
  [/^\/admin\/users\/[^/]+$/, 'Admin · User detail'],
  [/^\/admin\/errors\/[^/]+$/, 'Admin · Error detail'],
  [/^\/admin\/session\/[^/]+$/, 'Admin · Session recording'],
  [/^\/admin\/customers\/[^/]+$/, 'Admin · Customers'],
  [/^\/admin$/, 'Admin'],
  [/^\/p\/[^/]+\/journeys\/[^/]+$/, 'Embedded journey'],
  [/^\/p\/[^/]+(\/[^/]+)?$/, 'Embedded project'],
]

function pageRouteGroup(urlPath: string | null): string {
  if (!urlPath) return 'Other pages'
  const path = urlPath.split(/[?#]/)[0]!.replace(/\/+$/, '') || '/'
  for (const [re, name] of PAGE_ROUTE_GROUPS) {
    const m = path.match(re)
    if (m) return typeof name === 'function' ? name(m) : name
  }
  return path
}

function eventBucket(e: AnalyticsEventEntry): { key: string; label: string } {
  if (e.eventType === 1) {
    const group = pageRouteGroup(e.urlPath)
    return { key: `page:${group}`, label: group }
  }
  const name = e.eventName ?? 'event'
  const target = e.tag && e.tag.trim() ? e.tag.trim() : null
  return target ? { key: `evt:${name}:${target}`, label: target } : { key: `evt:${name}`, label: name }
}

// Group one visitor's analytics events into the same activity buckets the Events section uses,
// busiest first. All occurrences share the same actor (this visitor), so the per-kind user count is
// always 1 and the consuming view hides it.
function groupVisitorEventsByKind(
  events: AnalyticsEventEntry[],
  actor: { userId: string | null; label: string; anonymous: boolean; ip: string | null },
): EventKind[] {
  const map = new Map<string, EventKind>()
  for (const e of events) {
    const { key, label } = eventBucket(e)
    let k = map.get(key)
    if (!k) {
      k = { key, kind: label, eventType: e.eventType, eventName: e.eventName, count: 0, userCount: 1, occurrences: [] }
      map.set(key, k)
    }
    k.count += 1
    // A per-event `anonymous` flag (set on the user detail page for pre-sign-in events) overrides the
    // actor: such an occurrence is labelled "Anonymous" with no user link, even though the surrounding
    // feed belongs to a known user.
    const anonymous = e.anonymous ?? actor.anonymous
    k.occurrences.push({
      id: e.id, createdAt: e.createdAt,
      userId: anonymous ? null : actor.userId,
      label: anonymous && !actor.anonymous ? 'Anonymous (pre-sign-in)' : actor.label,
      anonymous, ip: actor.ip, urlPath: e.urlPath, tag: e.tag,
    })
  }
  return [...map.values()].sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind))
}

// A visitor's events, grouped into activity buckets exactly like the day-level Events section, with a
// truncation note when the server capped how many events it sent for this visitor.
export function VisitorEvents({ events, eventCount, actor }: {
  events: AnalyticsEventEntry[]
  eventCount: number
  actor: { userId: string | null; label: string; anonymous: boolean; ip: string | null }
}) {
  if (events.length === 0) return <div className="settings-admin__logs-empty">No recorded events this day.</div>
  return (
    <>
      <DayEventsByKind kinds={groupVisitorEventsByKind(events, actor)} showUsers={false} />
      {eventCount > events.length && (
        <div className="settings-admin__logs-empty">Showing latest {events.length} of {eventCount} events</div>
      )}
    </>
  )
}

// An expandable row whose header is split into a toggle button (left) and a separate user link
// (right), so a logged-in/active user can both reveal their events and link to their history page
// without nesting a link inside the toggle button.
function ActiveUserRow({ user }: { user: ActiveUser }) {
  const [open, setOpen] = useState(false)
  const bits = [`${user.sessionCount} session${user.sessionCount === 1 ? '' : 's'}`]
  if (user.eventCount > 0) bits.push(`${user.eventCount} event${user.eventCount === 1 ? '' : 's'}`)
  if (user.signupAt) bits.push(`joined ${timeAgo(user.signupAt)}`)
  if (user.lastSeenAt) bits.push(`last seen ${timeAgo(user.lastSeenAt)}`)
  return (
    <div className="settings-admin__expandable">
      <div className="settings-admin__expandable-header settings-admin__expandable-header--split">
        <button type="button" className="settings-admin__expandable-toggle" onClick={() => setOpen(!open)} data-analytics-event="admin-activity-active-user-row">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="settings-admin__expandable-title">{userLabel(user)}</span>
          <span className="settings-admin__expandable-sub">{bits.join(' · ')}</span>
        </button>
        <span className="settings-admin__expandable-actions">
          <UserLink userId={user.userId} label="History" />
        </span>
      </div>
      {/* Admin-only AI summary of the user's activity this day, shown inline when one exists. */}
      {user.summary && (
        <p className="settings-admin__activity-summary" data-testid="active-user-activity-summary">
          <Sparkles size={12} aria-hidden style={{ verticalAlign: '-2px', marginRight: 4 }} />
          {user.summary}
        </p>
      )}
      {open && (
        <div className="settings-admin__expandable-body">
          <VisitorEvents
            events={user.events}
            eventCount={user.eventCount}
            actor={{ userId: user.userId, label: userLabel(user), anonymous: false, ip: null }}
          />
        </div>
      )}
    </div>
  )
}

function AnonymousVisitorRow({ visitor }: { visitor: AnonVisitor }) {
  const deviceBits = [visitor.browser, visitor.os].filter(Boolean).join(' / ')
  const subtitle = [
    `${visitor.eventCount} event${visitor.eventCount === 1 ? '' : 's'}`,
    `${visitor.pageviewCount} pageview${visitor.pageviewCount === 1 ? '' : 's'}`,
    `${visitor.sessionCount} session${visitor.sessionCount === 1 ? '' : 's'}`,
    visitor.linkedUserId ? '↳ signed in later' : null,
  ].filter(Boolean).join(' · ')
  return (
    <ExpandableContainer title={visitor.ip ?? 'unknown IP'} subtitle={subtitle}>
      <div className="settings-admin__container-detail">
        <div>
          Anonymous visitor — IP: <span className="settings-admin__mono">{visitor.ip ?? 'unknown'}</span>
          {deviceBits && <> · {deviceBits}</>}
        </div>
        {visitor.linkedUserId && visitor.linkedUserLabel && (
          <div>
            Later signed in as{' '}
            <UserLink userId={visitor.linkedUserId} label={visitor.linkedUserLabel} />
          </div>
        )}
        <VisitorEvents
          events={visitor.events}
          eventCount={visitor.eventCount}
          actor={{ userId: null, label: `Anonymous (${visitor.ip ?? 'unknown IP'})`, anonymous: true, ip: visitor.ip }}
        />
      </div>
    </ExpandableContainer>
  )
}

// Icon for an event kind, matching the per-event icons used in the event logs: an eye for
// pageviews, a keyboard for input changes, a pointer for other custom events.
function eventKindIcon(kind: EventKind) {
  if (kind.eventType === 1) return <Eye size={12} aria-hidden />
  if (kind.eventName === 'input-change') return <Keyboard size={12} aria-hidden />
  return <MousePointerClick size={12} aria-hidden />
}

// One filled star per importance level (1–3), used to mark flagged event buckets across the admin
// displays. Renders nothing below level 1. The level scale matches MAX_IMPORTANCE in lib/event-flags.
export function ImportanceStars({ level, size = 12 }: { level: number; size?: number }) {
  if (!level || level < 1) return null
  return (
    <span
      className="settings-admin__importance-stars"
      aria-label={`Importance level ${level}`}
      title={`Importance level ${level} of 3`}
    >
      {Array.from({ length: Math.min(level, 3) }, (_, i) => (
        <Star key={i} size={size} className="settings-admin__important-star" aria-hidden />
      ))}
    </span>
  )
}

// Human-readable label for an importance level, used in tooltips and the picker menu.
export function importanceLabel(level: number): string {
  if (level <= IMPORTANCE_UNSET) return 'Not set'
  if (level === 0) return 'Priority 0'
  return `Priority ${level}`
}

// Visual mark for an event bucket's importance: a hollow circle at the explicit level 0, one-to-three
// filled stars at levels 1–3, and nothing when not set. The level-0 circle is deliberately distinct
// from the empty (not-set) state so a bucket explicitly pinned at 0 still reads as annotated.
export function ImportanceMark({ level, size = 12 }: { level: number; size?: number }) {
  if (level <= IMPORTANCE_UNSET) return null
  if (level === 0) {
    return (
      <span className="settings-admin__importance-stars" aria-label={importanceLabel(level)} title={importanceLabel(level)}>
        <Circle size={size} className="settings-admin__important-zero" aria-hidden />
      </span>
    )
  }
  return <ImportanceStars level={level} size={size} />
}

// Inline importance picker shown on every event row so an admin can set a bucket's priority directly,
// without opening its history page. The trigger shows the current mark (or a faint outline when not
// set); clicking opens a small menu of choices (3/2/1/0/Not set). Selecting one persists immediately
// and refreshes the shared levels cache so all displays re-mark/re-sort. Mirrors the InlineStatusDropdown
// pattern (BugTable): an outside-click closes the menu.
export function ImportancePicker({ eventKey, level }: { eventKey: string; level: number }) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // The open menu is positioned with `position: fixed` (coords computed from the trigger) so it is NOT
  // clipped by the row's `.settings-admin__expandable { overflow: hidden }` ancestor — a collapsed row
  // is only ~one line tall, so the absolutely-positioned menu dropping below it was cut off entirely
  // (invisible, and its options not the click target — clicks fell through to the trigger/next row, so
  // changing priority appeared to do nothing). The menu stays a DOM child of `ref`, so the outside-click
  // logic below and the option onClick handlers keep working. Right-aligned to the trigger to mirror the
  // former `right: 0` styling, and repositioned on scroll/resize so it tracks the trigger while open.
  const [menuStyle, setMenuStyle] = useState<{ top: number; right: number } | null>(null)

  const reposition = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setMenuStyle({ top: r.bottom + 4, right: Math.max(0, window.innerWidth - r.right) })
  }, [])

  useEffect(() => {
    if (!open) return
    reposition()
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('resize', reposition)
    // Capture phase so scrolling any ancestor scroll container repositions the menu, not just window.
    window.addEventListener('scroll', reposition, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  const choose = useCallback(async (next: number) => {
    setOpen(false)
    if (next === level) return
    setSaving(true)
    await setEventImportanceLevel(eventKey, next)
    setSaving(false)
  }, [eventKey, level])

  const isSet = level > IMPORTANCE_UNSET
  return (
    <div className="settings-admin__importance-picker" ref={ref} data-testid={`event-importance-picker-${eventKey}`}>
      <button
        type="button"
        ref={triggerRef}
        className="settings-admin__importance-trigger"
        onClick={() => { if (!open) reposition(); setOpen(o => !o) }}
        disabled={saving}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Priority: ${importanceLabel(level)} — click to change`}
        data-testid={`event-importance-trigger-${eventKey}`}
        data-analytics-event="admin-event-importance-trigger"
      >
        {isSet
          ? <ImportanceMark level={level} />
          : <Star size={12} className="settings-admin__importance-unset-icon" aria-hidden />}
        <span className="settings-admin__importance-trigger-label">{importanceLabel(level)}</span>
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && (
        <div
          className="settings-admin__importance-menu settings-admin__importance-menu--fixed"
          role="menu"
          aria-label="Set priority"
          style={menuStyle ? { top: menuStyle.top, right: menuStyle.right } : undefined}
        >
          {IMPORTANCE_CHOICES.map(choice => (
            <button
              type="button"
              key={choice}
              className={`settings-admin__importance-option${choice === level ? ' settings-admin__importance-option--active' : ''}`}
              onClick={() => choose(choice)}
              role="menuitem"
              data-testid={`event-importance-option-${choice}-${eventKey}`}
              data-analytics-event="admin-event-importance-set"
            >
              <span className="settings-admin__importance-option-mark">
                {choice > IMPORTANCE_UNSET
                  ? <ImportanceMark level={choice} />
                  : <Star size={12} className="settings-admin__importance-unset-icon" aria-hidden />}
              </span>
              {importanceLabel(choice)}
              {choice === level && <CheckCircle2 size={12} aria-hidden style={{ marginLeft: 'auto' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// One occurrence of an event kind: its timestamp, the user/visitor who triggered it (linking to the
// user's history page when known), and light context (the path it happened on and any tag).
export function EventOccurrenceRow({ occ }: { occ: EventOccurrence }) {
  const context = [occ.urlPath, occ.tag].filter(Boolean).join(' · ')
  return (
    <div className="settings-admin__log settings-admin__log--event" data-testid="event-occurrence">
      <span className="settings-admin__log-time">{formatTimestamp(occ.createdAt)}</span>
      <span className="settings-admin__log-msg">
        {occ.anonymous || !occ.userId
          ? <span className="settings-admin__mono">{occ.label}</span>
          : <UserLink userId={occ.userId} label={occ.label} />}
        {context && <span className="settings-admin__item-id"> {context}</span>}
      </span>
    </div>
  )
}

// A compact link to an event bucket's history/detail page, shown on every event row so an admin can
// jump from any event display to that event's timeline (and flag it important).
export function EventDetailLink({ eventKey, label = 'History' }: { eventKey: string; label?: string }) {
  return (
    <Link to={eventDetailUrl(eventKey)} className="settings-admin__user-link" data-analytics-event="admin-event-history-link">
      <History size={12} aria-hidden />
      {label}
    </Link>
  )
}

// One activity bucket for a day (a named page-route group, or a targeted element/feature), expandable
// to its occurrences — each showing when it happened and the associated user. Occurrences may be
// truncated server-side; the header always shows the true total count. Buckets carry an importance
// `level` (0–3, or IMPORTANCE_UNSET): the level is shown and adjusted inline via the header's priority
// picker (no need to open the history page), and every row also links to the bucket's history page.
// When the bucket maps to a known event, a short description is shown above its occurrences.
function EventKindRow({ kind, showUsers = true, level = IMPORTANCE_UNSET }: { kind: EventKind; showUsers?: boolean; level?: number }) {
  const subtitle = [
    `${kind.count} event${kind.count === 1 ? '' : 's'}`,
    ...(showUsers ? [`${kind.userCount} user${kind.userCount === 1 ? '' : 's'}`] : []),
  ].join(' · ')
  // The priority is shown (and edited) solely via the ImportancePicker in the actions below; we no
  // longer also stamp an ImportanceMark on the leading icon, which produced two identical priority
  // indicators on the same row.
  const icon = eventKindIcon(kind)
  const description = eventDescription(kind.kind)
  const actions = (
    <>
      <ImportancePicker eventKey={kind.key} level={level} />
      <EventDetailLink eventKey={kind.key} />
    </>
  )
  return (
    <ExpandableContainer icon={icon} title={kind.kind} subtitle={subtitle} actions={actions}>
      {description && <div className="settings-admin__event-description">{description}</div>}
      <div className="settings-admin__logs">
        {kind.occurrences.map(occ => <EventOccurrenceRow key={occ.id} occ={occ} />)}
        {kind.count > kind.occurrences.length && (
          <div className="settings-admin__logs-empty">
            Showing latest {kind.occurrences.length} of {kind.count} events
          </div>
        )}
      </div>
    </ExpandableContainer>
  )
}

// A day's Events group: every analytics activity bucket for the day, each expandable to its
// occurrences. Admin-flagged "important" buckets are starred and sorted to the top (the server already
// orders this way for its feeds; we re-apply it here so client-grouped views and freshly-toggled flags
// stay consistent), otherwise the incoming busiest-first order is preserved.
export function DayEventsByKind({ kinds, showUsers = true }: { kinds: EventKind[]; showUsers?: boolean }) {
  const importantLevels = useImportantEventLevels()
  // Take the higher of the server-stamped level and the freshly-fetched hook level, so a just-toggled
  // flag re-marks/re-sorts client-grouped views (and freshly-changed levels) before the feed refetches.
  // IMPORTANCE_UNSET (-1) is the floor, so an explicit level 0 sorts above a not-set bucket.
  const levelOf = (k: EventKind) => Math.max(k.important ?? IMPORTANCE_UNSET, importantLevels.get(k.key) ?? IMPORTANCE_UNSET)
  const ordered = kinds
    .map((k, i) => ({ k, i }))
    .sort((a, b) => levelOf(b.k) - levelOf(a.k) || a.i - b.i)
    .map(({ k }) => k)
  return (
    <div className="settings-admin__user-projects" data-testid="day-events-by-kind">
      {ordered.map(k => <EventKindRow key={k.key} kind={k} showUsers={showUsers} level={levelOf(k)} />)}
    </div>
  )
}

// One changed project, expandable to its recorded field-level change history. Shows what was
// modified and the old→new value for each field across the window.
function ChangedProjectRow({ project }: { project: ChangedProjectEntry }) {
  const owner = project.ownerEmail ?? project.ownerId ?? 'unknown'
  const changeCount = project.changes.length
  const subtitle = changeCount > 0
    ? `${owner} · ${changeCount} change${changeCount === 1 ? '' : 's'}`
    : owner
  return (
    <ExpandableContainer title={project.name} subtitle={subtitle}>
      <div className="settings-admin__container-detail">
        <div className="settings-admin__change-meta">
          <Link to={`/admin/projects/${project.id}`} className="settings-admin__user-project-name" data-analytics-event="admin-activity-changed-project-link">Open project</Link>
          <ProjectStats bugCount={project.bugCount ?? 0} testRunCount={project.testRunCount ?? 0} />
          {project.createdAt && <ProjectDuration createdAt={project.createdAt} finishedAt={project.finishedAt ?? null} inProgress={project.inProgress ?? false} />}
          <CompletionEmailIcon projectId={project.id} sentAt={project.completionEmailSentAt ?? null} />
          {project.ownerId && <UserLink userId={project.ownerId} label={owner} />}
        </div>
        {changeCount === 0 ? (
          <div className="settings-admin__logs-empty">
            No field-level changes recorded — the project row was touched but no change detail was captured.
          </div>
        ) : (
          <div className="settings-admin__changes">
            {project.changes.map((c, i) => (
              <div key={i} className="settings-admin__change">
                <div className="settings-admin__change-time">{formatTimestamp(c.performedAt)}</div>
                {c.fields.map(f => (
                  <div key={f.field} className="settings-admin__change-field">
                    <span className="settings-admin__change-name">{f.field}</span>
                    <span className="settings-admin__change-old">{f.old ?? '∅'}</span>
                    <span className="settings-admin__change-arrow">→</span>
                    <span className="settings-admin__change-new">{f.new ?? '∅'}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </ExpandableContainer>
  )
}

// One task that errored, nested under its project. Collapsed it shows a failed/passed badge, the
// goal, and an error count; expanded it lists the individual Replay MCP errors and links to the
// task page. Failed tasks with no MCP errors still appear (with a note) so failures aren't hidden.
export function TaskErrorRow({ task, projectId }: { task: ErrorTask; projectId: string }) {
  const [open, setOpen] = useState(false)
  const errorLabel = task.errors.length > 0
    ? `${task.errors.length} MCP error${task.errors.length === 1 ? '' : 's'}`
    : 'no MCP errors'
  return (
    <div className="settings-admin__expandable">
      <button type="button" className="settings-admin__expandable-header" onClick={() => setOpen(!open)} data-testid={`error-task-${task.id}`} data-analytics-event="admin-errors-task-row">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className={`settings-admin__badge ${task.failed ? 'settings-admin__badge--unreachable' : 'settings-admin__badge--idle'}`}>
          {task.failed ? 'failed' : 'passed'}
        </span>
        <span className="settings-admin__expandable-title">{task.goal ?? task.id}</span>
        <span className="settings-admin__expandable-sub">{errorLabel} · {timeAgo(task.lastActivityAt)}</span>
      </button>
      {open && (
        <div className="settings-admin__expandable-body">
          <div className="settings-admin__container-detail">
            <div>
              <Link to={`/projects/${projectId}/tasks/${task.id}`} className="settings-admin__user-project-name" data-analytics-event="admin-errors-task-page-link">View task page</Link>
            </div>
            {task.errors.length > 0 ? (
              <div className="settings-admin__logs">
                <div className="settings-admin__logs-title">Replay MCP errors ({task.errors.length})</div>
                {task.errors.map(e => (
                  <Link key={e.id} to={adminErrorUrl(e.id)} className="settings-admin__log settings-admin__log--error settings-admin__log--link" data-analytics-event="admin-errors-task-error-link">
                    <span className="settings-admin__log-time">{formatTimestamp(e.occurredAt)}</span>
                    <span className="settings-admin__log-type">{e.kind ?? 'error'}</span>
                    <span className="settings-admin__log-msg">
                      {e.tool ? `[${e.tool}] ` : ''}{e.message ?? '(no message)'}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="settings-admin__logs-empty">
                Task failed with no Replay MCP errors recorded — check the task page for details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// One MCP error pulled out of the project → task → error nesting so a day's errors can be regrouped
// by tool and message; keeps the task/project it came from for linking.
interface McpErrorOccurrence {
  taskId: string
  goal: string | null
  projectId: string
  projectName: string
  // The light error summary the feed sends (id/tool/message/kind/time) — enough to list the
  // occurrence and link to its detail page, where the full record (params, recording, timing) loads.
  error: TaskErrorDetail
}

interface ErrorMessageGroup {
  message: string | null
  occurrences: McpErrorOccurrence[]
}

interface ToolErrorGroup {
  tool: string | null
  count: number
  messages: ErrorMessageGroup[]
}

// Regroup a day's Replay MCP errors (stored nested under project → task) by tool name, then by error
// message. Tools and messages are ordered by occurrence count, most frequent first. Sentinel keys
// (…) keep a null tool/message as its own group without colliding with a real value.
export function groupDayErrorsByTool(projects: ProjectErrors[]): ToolErrorGroup[] {
  const tools = new Map<string, { tool: string | null; count: number; messages: Map<string, ErrorMessageGroup> }>()
  for (const p of projects) {
    for (const t of p.tasks) {
      for (const e of t.errors) {
        // The MCP errors section is Replay-MCP-only (source 'mcp'). Other task_errors sources are the
        // failure records failTask files for every failed task (source 'agent'/'reaper'/…); those belong
        // to the Failed tasks section, not here. Including them made this section's expanded list dwarf
        // its own header count (which only ever counted MCP errors).
        if (e.source !== 'mcp') continue
        const toolKey = e.tool ?? ' unknown'
        let tg = tools.get(toolKey)
        if (!tg) { tg = { tool: e.tool, count: 0, messages: new Map() }; tools.set(toolKey, tg) }
        tg.count++
        const msgKey = e.message ?? ' none'
        let mg = tg.messages.get(msgKey)
        if (!mg) { mg = { message: e.message, occurrences: [] }; tg.messages.set(msgKey, mg) }
        mg.occurrences.push({
          taskId: t.id, goal: t.goal, projectId: p.projectId, projectName: p.projectName, error: e,
        })
      }
    }
  }
  return [...tools.values()]
    .map(tg => ({
      tool: tg.tool,
      count: tg.count,
      messages: [...tg.messages.values()]
        // Within a message, show the newest occurrence first — matching the newest-first ordering
        // used for failed tasks and event occurrences. The occurrences were collected in project →
        // task grouping order (not by time), so without this an error stays wherever its task landed;
        // since only the first OCCURRENCES_PAGE_SIZE render before "Load more", a recent error could
        // fall past the first page and appear missing from the expanded list. occurredAt is an ISO
        // string, so a lexicographic compare is chronological.
        .map(mg => ({
          message: mg.message,
          occurrences: [...mg.occurrences].sort((a, b) => b.error.occurredAt.localeCompare(a.error.occurredAt)),
        }))
        .sort((a, b) => b.occurrences.length - a.occurrences.length),
    }))
    .sort((a, b) => b.count - a.count)
}

// Total Replay MCP error occurrences (source 'mcp') across the given projects' tasks — the number the
// "MCP errors" section shows. This counts occurrences, not tasks: one task that hit 40 MCP errors
// contributes 40. The old header used tasks-with-an-MCP-error, so it read far lower than the list it
// expanded to. Non-MCP task errors are excluded — they're failure records, surfaced as Failed tasks.
export function mcpOccurrenceCount(projects: ProjectErrors[]): number {
  return projects.reduce((n, p) => n + p.tasks.reduce((m, t) => m + t.errors.filter(e => e.source === 'mcp').length, 0), 0)
}

// All failed tasks for the day (across projects), regardless of whether they also hit MCP errors.
function allFailedTasks(projects: ProjectErrors[]): { task: ErrorTask; projectId: string }[] {
  const out: { task: ErrorTask; projectId: string }[] = []
  for (const p of projects) {
    for (const t of p.tasks) {
      if (t.failed) out.push({ task: t, projectId: p.projectId })
    }
  }
  return out
}

// How many error occurrences a message group shows before the "Load more" button (the rest stay
// hidden until requested, so a message hit thousands of times doesn't render its whole list at once).
const OCCURRENCES_PAGE_SIZE = 100

// One MCP error occurrence, rendered as a link (timestamp, kind, and the task it came from) to that
// error's own detail page — where its params, recording, timing, and full stored record live —
// rather than expanding inline.
function McpErrorOccurrenceRow({ occ }: { occ: McpErrorOccurrence }) {
  const e = occ.error
  return (
    <Link
      to={adminErrorUrl(e.id)}
      className="settings-admin__error-occurrence settings-admin__error-occurrence-header settings-admin__error-occurrence--link"
      data-testid="mcp-error-occurrence"
      data-analytics-event="admin-errors-occurrence-link"
    >
      <span className="settings-admin__log-time">{formatTimestamp(e.occurredAt)}</span>
      <span className="settings-admin__log-type">{e.kind ?? 'error'}</span>
      <span className="settings-admin__error-occurrence-task">{occ.goal ?? occ.taskId}</span>
      <ChevronRight size={13} className="settings-admin__error-occurrence-chevron" aria-hidden />
    </Link>
  )
}

// One error message within a tool group, expanding to its individual occurrences. Only the first
// OCCURRENCES_PAGE_SIZE are rendered; a "Load more" button reveals the next page on demand.
function ErrorMessageRow({ group }: { group: ErrorMessageGroup }) {
  const [open, setOpen] = useState(false)
  const [limit, setLimit] = useState(OCCURRENCES_PAGE_SIZE)
  const n = group.occurrences.length
  const shown = group.occurrences.slice(0, limit)
  return (
    <div className="settings-admin__expandable">
      <button type="button" className="settings-admin__expandable-header" onClick={() => setOpen(!open)} data-testid="error-message-group" data-analytics-event="admin-errors-message-row">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="settings-admin__expandable-title">{group.message ?? '(no message)'}</span>
        <span className="settings-admin__expandable-sub">{n} occurrence{n === 1 ? '' : 's'}</span>
      </button>
      {open && (
        <div className="settings-admin__expandable-body">
          <div className="settings-admin__logs">
            {shown.map(occ => <McpErrorOccurrenceRow key={occ.error.id} occ={occ} />)}
          </div>
          {n > limit && (
            <button
              type="button"
              className="settings-admin__load-more"
              data-testid="error-load-more"
              onClick={() => setLimit(l => l + OCCURRENCES_PAGE_SIZE)}
              data-analytics-event="admin-errors-load-more-button"
            >
              Load more ({n - limit} more)
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// One tool's errors for a day, expanding to the per-message groups beneath it.
function ToolErrorGroupRow({ group }: { group: ToolErrorGroup }) {
  return (
    <ExpandableContainer
      title={group.tool ?? '(unknown tool)'}
      subtitle={`${group.count} error${group.count === 1 ? '' : 's'} · ${group.messages.length} message${group.messages.length === 1 ? '' : 's'}`}
    >
      <div className="settings-admin__error-tasks">
        {group.messages.map((m, i) => <ErrorMessageRow key={i} group={m} />)}
      </div>
    </ExpandableContainer>
  )
}

// A day's Replay MCP errors grouped by tool name and then by message. (Failed tasks are shown in their
// own subsection — see DayFailedTasks — so a failure isn't hidden even with no MCP errors.)
export function DayErrorsByTool({ projects }: { projects: ProjectErrors[] }) {
  const toolGroups = groupDayErrorsByTool(projects)
  return (
    <div className="settings-admin__user-projects" data-testid="day-errors-by-tool">
      {toolGroups.length === 0
        ? <p className="settings-admin__logs-empty">No Replay MCP errors recorded.</p>
        : toolGroups.map((g, i) => <ToolErrorGroupRow key={i} group={g} />)}
    </div>
  )
}

// One project's MCP errors broken down by source (tool), reusing the same tool→message→occurrence
// rendering as the day-level "by source" view but scoped to a single project's tasks.
function ProjectErrorsBySourceRow({ project }: { project: ProjectErrors }) {
  const owner = project.ownerEmail ?? project.ownerId ?? 'unknown owner'
  const toolGroups = groupDayErrorsByTool([project])
  if (toolGroups.length === 0) return null
  const mcpCount = toolGroups.reduce((n, g) => n + g.count, 0)
  return (
    <ExpandableContainer title={project.projectName} subtitle={`${owner} · ${mcpCount} MCP error${mcpCount === 1 ? '' : 's'}`}>
      <div className="settings-admin__error-tasks">
        {toolGroups.map((g, i) => <ToolErrorGroupRow key={i} group={g} />)}
      </div>
    </ExpandableContainer>
  )
}

// A day's Errors grouped by project: every project with errors that day, each expanding to its
// errors broken down by source (the same tool grouping the "by source" view uses).
export function DayErrorsByProject({ projects }: { projects: ProjectErrors[] }) {
  const withMcpErrors = projects.filter(p => mcpOccurrenceCount([p]) > 0)
  return (
    <div className="settings-admin__user-projects" data-testid="day-errors-by-project">
      {withMcpErrors.length === 0
        ? <p className="settings-admin__logs-empty">No Replay MCP errors recorded.</p>
        : withMcpErrors.map(p => <ProjectErrorsBySourceRow key={p.projectId} project={p} />)}
    </div>
  )
}

// The day's failed tasks (across projects), newest activity first, each expanding to its error records
// and a link to the task page. Failures are no longer their own categorization pipeline — they bucket
// like any other error via error-processing; use the Buckets view to see them grouped by bucket.
export function DayFailedTasks({ projects }: { projects: ProjectErrors[] }) {
  const failed = allFailedTasks(projects).sort((a, b) => b.task.lastActivityAt.localeCompare(a.task.lastActivityAt))
  if (failed.length === 0) return <p className="settings-admin__logs-empty">No failed tasks.</p>
  return (
    <div className="settings-admin__user-projects" data-testid="day-failed-tasks">
      <div className="settings-admin__error-tasks">
        {failed.map(({ task, projectId }) => <TaskErrorRow key={task.id} task={task} projectId={projectId} />)}
      </div>
    </div>
  )
}

// The day's container spawn failures, grouped by project (warm-pool failures arrive under a synthetic
// "Warm pool" project). Each project expands to its individual spawn-failed events (reason + time), so
// a project unable to get workers is visible alongside failed tasks and MCP errors.
export function DaySpawnFailures({ projects }: { projects: ProjectErrors[] }) {
  const withSpawnFailures = projects.filter(p => p.spawnFailureCount > 0)
  if (withSpawnFailures.length === 0) return <p className="settings-admin__logs-empty">No container spawn failures.</p>
  return (
    <div className="settings-admin__user-projects" data-testid="day-spawn-failures">
      {withSpawnFailures.map(p => {
        const owner = p.ownerEmail ?? p.ownerId ?? 'no owner'
        return (
          <ExpandableContainer
            key={p.projectId}
            title={p.projectName}
            subtitle={`${owner} · ${p.spawnFailureCount} failure${p.spawnFailureCount === 1 ? '' : 's'}`}
          >
            <div className="settings-admin__logs">
              {p.spawnFailures.map(f => (
                <Link
                  key={f.id}
                  to={adminErrorUrl(f.id)}
                  className="settings-admin__log settings-admin__log--error settings-admin__log--link"
                  data-testid="spawn-failure-occurrence"
                  data-analytics-event="admin-errors-spawn-failure-link"
                >
                  <span className="settings-admin__log-time">{formatTimestamp(f.occurredAt)}</span>
                  <span className="settings-admin__log-type">{f.role ?? 'spawn'}</span>
                  <span className="settings-admin__log-msg">{f.reason ?? `spawn failed (${f.name ?? f.machineId ?? 'container'})`}</span>
                  <ChevronRight size={13} className="settings-admin__error-occurrence-chevron" aria-hidden />
                </Link>
              ))}
            </div>
          </ExpandableContainer>
        )
      })}
    </div>
  )
}

// ── Admin Errors page ───────────────────────────────────────────────────────────────────────────
// The dedicated /admin/errors view reorganizes the same error data the Activity page's Errors section
// carries (ProjectErrors[]) into top-level collapsible sections — one per error source — each default
// collapsed. It reuses the Activity sections' visual language (the day-group toggle header, the
// expandable message rows, the log rows) so the two pages read as one system.

// A top-level collapsible section, styled like the Activity page's lazy sections (chevron + uppercase
// title + count). Default collapsed; the toggle is disabled when the count is zero. Presentational —
// its children are already in hand (the errors page loads all sections in one request), so unlike
// LazyActivitySection it fetches nothing on open.
export function CollapsibleSection({ icon, title, count, defaultOpen = false, testId, children }: {
  icon?: ReactNode
  title: string
  count: number
  defaultOpen?: boolean
  testId?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="settings-admin__day-group">
      <button
        type="button"
        className="settings-admin__day-group-title settings-admin__day-group-toggle"
        onClick={() => setOpen(o => !o)}
        disabled={count === 0}
        data-testid={testId}
        data-analytics-event="admin-errors-section-toggle"
      >
        {count > 0
          ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)
          : <span className="settings-admin__day-group-spacer" aria-hidden />}
        {icon}
        {title} ({count})
      </button>
      {open && count > 0 && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  )
}

// A message group (client/backend errors grouped by message text), collapsing to its occurrences.
// Mirrors the MCP ErrorMessageRow (same markup + testid) so grouped errors read identically across
// sources. Only the first OCCURRENCES_PAGE_SIZE occurrences render; "Load more" reveals the rest.
function GroupedErrorMessageRow<T extends { id: string }>({ message, occurrences, renderOccurrence }: {
  message: string
  occurrences: T[]
  renderOccurrence: (item: T) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [limit, setLimit] = useState(OCCURRENCES_PAGE_SIZE)
  const n = occurrences.length
  return (
    <div className="settings-admin__expandable">
      <button type="button" className="settings-admin__expandable-header" onClick={() => setOpen(!open)} data-testid="error-message-group" data-analytics-event="admin-errors-message-row">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="settings-admin__expandable-title">{message}</span>
        <span className="settings-admin__expandable-sub">{n} occurrence{n === 1 ? '' : 's'}</span>
      </button>
      {open && (
        <div className="settings-admin__expandable-body">
          <div className="settings-admin__logs">
            {occurrences.slice(0, limit).map(renderOccurrence)}
          </div>
          {n > limit && (
            <button
              type="button"
              className="settings-admin__load-more"
              data-testid="error-load-more"
              onClick={() => setLimit(l => l + OCCURRENCES_PAGE_SIZE)}
              data-analytics-event="admin-errors-load-more-button"
            >
              Load more ({n - limit} more)
            </button>
          )}
        </div>
      )}
    </div>
  )
}

interface MessageGroup<T> {
  message: string
  occurrences: T[]
}

// Group a flat list of errors by their display message text, most-frequent group first and, within a
// group, newest occurrence first (matching the MCP tool grouping). `messageOf` resolves the text a
// row is keyed and titled by; `timeOf` supplies the ISO timestamp used for the newest-first sort.
function groupByMessage<T>(items: T[], messageOf: (item: T) => string, timeOf: (item: T) => string): MessageGroup<T>[] {
  const groups = new Map<string, MessageGroup<T>>()
  for (const item of items) {
    const message = messageOf(item)
    let g = groups.get(message)
    if (!g) { g = { message, occurrences: [] }; groups.set(message, g) }
    g.occurrences.push(item)
  }
  return [...groups.values()]
    .map(g => ({ message: g.message, occurrences: [...g.occurrences].sort((a, b) => timeOf(b).localeCompare(timeOf(a))) }))
    .sort((a, b) => b.occurrences.length - a.occurrences.length)
}

// Client-side error events grouped by message text. Each message expands to its occurrences (time,
// event tag/name, path, user) — the same data DayClientErrors lists flat, regrouped for the errors page.
export function DayClientErrorsByMessage({ projects }: { projects: ProjectErrors[] }) {
  const all = projects.flatMap(p => p.clientErrors ?? [])
  const groups = groupByMessage(all, e => e.message ?? e.name ?? '(no message)', e => e.occurredAt)
  if (groups.length === 0) return <p className="settings-admin__logs-empty">No client errors.</p>
  return (
    <div className="settings-admin__user-projects" data-testid="day-client-errors">
      {groups.map((g, i) => (
        <GroupedErrorMessageRow
          key={i}
          message={g.message}
          occurrences={g.occurrences}
          renderOccurrence={e => (
            <Link
              key={e.id}
              to={adminErrorUrl(e.id)}
              className="settings-admin__log settings-admin__log--error settings-admin__log--link"
              data-testid="client-error-occurrence"
              data-analytics-event="admin-errors-client-error-link"
            >
              <span className="settings-admin__log-time">{formatTimestamp(e.occurredAt)}</span>
              <span className="settings-admin__log-type">{e.tag ?? e.name ?? 'error'}</span>
              <span className="settings-admin__log-msg">
                {e.urlPath ?? ''}
              </span>
              <ChevronRight size={13} className="settings-admin__error-occurrence-chevron" aria-hidden />
            </Link>
          )}
        />
      ))}
    </div>
  )
}

// Backend server-error records grouped by message text. Each message expands to its occurrences (time,
// HTTP status, handler path, and a Replay recording link when one was captured).
export function DayBackendErrorsByMessage({ projects }: { projects: ProjectErrors[] }) {
  const all = projects.flatMap(p => p.backendErrors ?? [])
  const groups = groupByMessage(all, e => e.message ?? e.handlerPath ?? '(server error)', e => e.occurredAt)
  if (groups.length === 0) return <p className="settings-admin__logs-empty">No backend errors.</p>
  return (
    <div className="settings-admin__user-projects" data-testid="day-backend-errors">
      {groups.map((g, i) => (
        <GroupedErrorMessageRow
          key={i}
          message={g.message}
          occurrences={g.occurrences}
          renderOccurrence={e => (
            <Link
              key={e.id}
              to={adminErrorUrl(e.id)}
              className="settings-admin__log settings-admin__log--error settings-admin__log--link"
              data-testid="backend-error-occurrence"
              data-analytics-event="admin-errors-backend-error-link"
            >
              <span className="settings-admin__log-time">{formatTimestamp(e.occurredAt)}</span>
              <span className="settings-admin__log-type">{e.errorSource ?? `HTTP ${e.httpStatusCode ?? '5xx'}`}</span>
              <span className="settings-admin__log-msg">
                {e.handlerPath ?? ''}{e.recordingId ? ` · ${e.recordingId}` : ''}
              </span>
              <ChevronRight size={13} className="settings-admin__error-occurrence-chevron" aria-hidden />
            </Link>
          )}
        />
      ))}
    </div>
  )
}

// The full Errors breakdown for the /admin/errors page: one top-level collapsible section per error
// source (MCP errors, failed tasks, container spawn failures, client errors, backend errors), each
// default collapsed. MCP errors keep the By source / By project toggle; client and backend errors are
// grouped by message text. Reuses the same rendering the Activity page's Errors section uses so the
// two stay consistent.
// Colors match the Activity chart's error/warning split (red errors, yellow warnings).
const SEVERITY_META: Record<ErrorSeverity, { label: string; color: string }> = {
  error: { label: 'Errors', color: '#ef4444' },
  warning: { label: 'Warnings', color: '#eab308' },
}

// Return a copy of a project's errors keeping only items of the given severity, with the roll-up
// counts recomputed, or null if nothing of that severity remains. Failed-task status and container
// spawn failures are always errors, so they only survive into the 'error' partition. A task's failure
// (Errors) and any recovered warnings it hit during its run (Warnings) can therefore both surface,
// each in its own group.
function filterProjectBySeverity(p: ProjectErrors, severity: ErrorSeverity): ProjectErrors | null {
  const isError = severity === 'error'
  const tasks: ErrorTask[] = []
  for (const t of p.tasks) {
    const errors = t.errors.filter(e => e.severity === severity)
    const failed = isError && t.failed
    if (failed || errors.length > 0) tasks.push({ ...t, failed, errors })
  }
  const spawnFailures = isError ? p.spawnFailures : []
  const clientErrors = p.clientErrors.filter(e => e.severity === severity)
  const backendErrors = p.backendErrors.filter(e => e.severity === severity)
  if (!tasks.length && !spawnFailures.length && !clientErrors.length && !backendErrors.length) return null
  return {
    ...p,
    tasks,
    spawnFailures,
    clientErrors,
    backendErrors,
    failedTaskCount: tasks.filter(t => t.failed).length,
    mcpErrorTaskCount: tasks.filter(t => t.errors.some(e => e.source === 'mcp')).length,
    spawnFailureCount: spawnFailures.length,
    clientErrorCount: clientErrors.length,
    backendErrorCount: backendErrors.length,
  }
}

function partitionBySeverity(projects: ProjectErrors[], severity: ErrorSeverity): ProjectErrors[] {
  return projects
    .map(p => filterProjectBySeverity(p, severity))
    .filter((p): p is ProjectErrors => p !== null)
}

function severityTotal(projects: ProjectErrors[]): number {
  return mcpOccurrenceCount(projects) + projects.reduce((n, p) => n + p.failedTaskCount + p.spawnFailureCount + (p.clientErrorCount ?? 0) + (p.backendErrorCount ?? 0), 0)
}

// The per-source subsections (MCP / failed tasks / spawn / client / backend) for one severity group.
// `idPrefix` namespaces the section test ids so the Errors and Warnings groups don't collide (the
// Errors group keeps the original `errors-*` ids).
function ErrorSourceSections({ projects, idPrefix }: { projects: ProjectErrors[]; idPrefix: string }) {
  const [view, setView] = useState<'source' | 'project'>('source')
  const failedCount = projects.reduce((n, p) => n + p.failedTaskCount, 0)
  const mcpErrorCount = mcpOccurrenceCount(projects)
  const spawnFailureCount = projects.reduce((n, p) => n + p.spawnFailureCount, 0)
  const clientErrorCount = projects.reduce((n, p) => n + (p.clientErrorCount ?? 0), 0)
  const backendErrorCount = projects.reduce((n, p) => n + (p.backendErrorCount ?? 0), 0)
  return (
    <div className="settings-admin__day-detail" data-testid={`${idPrefix}-source-sections`}>
      <CollapsibleSection icon={<Bug size={12} aria-hidden />} title="MCP errors" count={mcpErrorCount} testId={`${idPrefix}-section-mcp`}>
        <div className="settings-admin__chart-grouping" role="group" aria-label="Group MCP errors by" style={{ marginBottom: 8 }}>
          <button
            type="button"
            className={`settings-admin__chip ${view === 'source' ? 'settings-admin__chip--active' : ''}`}
            onClick={() => setView('source')}
            data-testid={`${idPrefix}-view-source`}
            aria-pressed={view === 'source'}
            data-analytics-event="admin-errors-view-source-toggle"
          >
            By source
          </button>
          <button
            type="button"
            className={`settings-admin__chip ${view === 'project' ? 'settings-admin__chip--active' : ''}`}
            onClick={() => setView('project')}
            data-testid={`${idPrefix}-view-project`}
            aria-pressed={view === 'project'}
            data-analytics-event="admin-errors-view-project-toggle"
          >
            By project
          </button>
        </div>
        {view === 'source'
          ? <DayErrorsByTool projects={projects} />
          : <DayErrorsByProject projects={projects} />}
      </CollapsibleSection>

      <CollapsibleSection icon={<AlertTriangle size={12} aria-hidden />} title="Failed tasks" count={failedCount} testId={`${idPrefix}-section-failed`}>
        <div style={{ marginBottom: 8 }}>
          <Link to="/admin/error-buckets" className="settings-admin__user-link" data-analytics-event="admin-errors-manage-buckets-link">
            Manage buckets
          </Link>
        </div>
        <DayFailedTasks projects={projects} />
      </CollapsibleSection>

      <CollapsibleSection icon={<Activity size={12} aria-hidden />} title="Container spawn failures" count={spawnFailureCount} testId={`${idPrefix}-section-spawn`}>
        <DaySpawnFailures projects={projects} />
      </CollapsibleSection>

      <CollapsibleSection icon={<MousePointerClick size={12} aria-hidden />} title="Client errors" count={clientErrorCount} testId={`${idPrefix}-section-client`}>
        <DayClientErrorsByMessage projects={projects} />
      </CollapsibleSection>

      <CollapsibleSection icon={<History size={12} aria-hidden />} title="Backend errors" count={backendErrorCount} testId={`${idPrefix}-section-backend`}>
        <DayBackendErrorsByMessage projects={projects} />
      </CollapsibleSection>
    </div>
  )
}

// One top-level severity group ("Errors" or "Warnings") with a colored heading and its per-source
// subsections. Shows an empty note when the group has nothing of that severity.
function ErrorSeverityGroup({ severity, projects, idPrefix }: { severity: ErrorSeverity; projects: ProjectErrors[]; idPrefix: string }) {
  const meta = SEVERITY_META[severity]
  const total = severityTotal(projects)
  return (
    <div className="settings-admin__errors-severity-group" data-testid={`errors-group-${severity}`} style={{ marginBottom: 16 }}>
      <div className="settings-admin__day-group-title" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 8 }}>
        <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: meta.color, display: 'inline-block' }} />
        {meta.label} ({total})
      </div>
      {total > 0
        ? <ErrorSourceSections projects={projects} idPrefix={idPrefix} />
        : <p className="settings-admin__logs-empty" data-testid={`errors-group-${severity}-empty`}>No {meta.label.toLowerCase()}.</p>}
    </div>
  )
}

// One error occurrence flattened out of the project → task/source nesting, tagged with its
// error-processing bucket, for the "By bucket" view.
interface FlatOccurrence { id: string; label: string; source: string; occurredAt: string; bucket: string | null; severity: ErrorSeverity }

function flattenOccurrences(projects: ProjectErrors[]): FlatOccurrence[] {
  const out: FlatOccurrence[] = []
  for (const p of projects) {
    for (const t of p.tasks) for (const e of t.errors) {
      out.push({ id: e.id, label: `${e.tool ? `[${e.tool}] ` : ''}${e.message ?? e.kind ?? 'error'}`, source: e.source, occurredAt: e.occurredAt, bucket: e.bucket ?? null, severity: e.severity })
    }
    for (const s of p.spawnFailures) out.push({ id: s.id, label: s.reason ?? 'spawn failure', source: 'spawn', occurredAt: s.occurredAt, bucket: s.bucket ?? null, severity: 'error' })
    for (const c of p.clientErrors) out.push({ id: c.id, label: c.message ?? c.name ?? 'client error', source: 'client', occurredAt: c.occurredAt, bucket: c.bucket ?? null, severity: c.severity })
    for (const be of p.backendErrors) out.push({ id: be.id, label: be.message ?? be.errorSource ?? `HTTP ${be.httpStatusCode ?? ''}`, source: 'backend', occurredAt: be.occurredAt, bucket: be.bucket ?? null, severity: be.severity })
  }
  return out
}

const UNBUCKETED_LABEL = 'Unbucketed (pending)'

// All errors + warnings across sources, grouped by their error-processing bucket. Most-populated
// bucket first; unbucketed (not yet processed by the pipeline) sorts last.
export function BucketGroupedErrors({ projects }: { projects: ProjectErrors[] }) {
  const byBucket = new Map<string, FlatOccurrence[]>()
  for (const o of flattenOccurrences(projects)) {
    const key = o.bucket ?? UNBUCKETED_LABEL
    if (!byBucket.has(key)) byBucket.set(key, [])
    byBucket.get(key)!.push(o)
  }
  const groups = [...byBucket.entries()]
    .map(([bucket, occurrences]) => ({ bucket, occurrences: occurrences.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)) }))
    .sort((a, b) => {
      if (a.bucket === UNBUCKETED_LABEL) return 1
      if (b.bucket === UNBUCKETED_LABEL) return -1
      return b.occurrences.length - a.occurrences.length
    })
  if (groups.length === 0) return <p className="settings-admin__logs-empty">No errors.</p>
  return (
    <div className="settings-admin__day-detail" data-testid="errors-by-bucket">
      {groups.map(g => (
        <CollapsibleSection key={g.bucket} icon={<Bug size={12} aria-hidden />} title={g.bucket} count={g.occurrences.length} testId={`errors-bucket-${g.bucket}`}>
          <div className="settings-admin__logs">
            {g.occurrences.slice(0, OCCURRENCES_PAGE_SIZE).map(o => (
              <Link key={o.id} to={adminErrorUrl(o.id)} className={`settings-admin__log settings-admin__log--${o.severity === 'warning' ? 'warning' : 'error'} settings-admin__log--link`} data-analytics-event="admin-errors-bucket-occurrence-link">
                <span className="settings-admin__log-time">{formatTimestamp(o.occurredAt)}</span>
                <span className="settings-admin__log-type">{o.source}</span>
                <span className="settings-admin__log-msg">{o.label}</span>
              </Link>
            ))}
            {g.occurrences.length > OCCURRENCES_PAGE_SIZE && (
              <p className="settings-admin__logs-empty">+{g.occurrences.length - OCCURRENCES_PAGE_SIZE} more — see the Error buckets page</p>
            )}
          </div>
        </CollapsibleSection>
      ))}
    </div>
  )
}

export function ErrorsBreakdown({ projects }: { projects: ProjectErrors[] }) {
  // Top-level grouping: the per-source severity breakdown (default) or all errors grouped by their
  // error-processing bucket.
  const [groupBy, setGroupBy] = useState<'source' | 'bucket'>('source')
  const errorProjects = partitionBySeverity(projects, 'error')
  const warningProjects = partitionBySeverity(projects, 'warning')
  return (
    <div data-testid="errors-breakdown">
      <div className="settings-admin__chart-grouping" role="group" aria-label="Group errors by" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`settings-admin__chip ${groupBy === 'source' ? 'settings-admin__chip--active' : ''}`}
          onClick={() => setGroupBy('source')}
          aria-pressed={groupBy === 'source'}
          data-testid="errors-groupby-source"
          data-analytics-event="admin-errors-groupby-source-toggle"
        >
          By source
        </button>
        <button
          type="button"
          className={`settings-admin__chip ${groupBy === 'bucket' ? 'settings-admin__chip--active' : ''}`}
          onClick={() => setGroupBy('bucket')}
          aria-pressed={groupBy === 'bucket'}
          data-testid="errors-groupby-bucket"
          data-analytics-event="admin-errors-groupby-bucket-toggle"
        >
          By bucket
        </button>
      </div>
      {groupBy === 'source' ? (
        <>
          <ErrorSeverityGroup severity="error" projects={errorProjects} idPrefix="errors" />
          <ErrorSeverityGroup severity="warning" projects={warningProjects} idPrefix="warnings" />
        </>
      ) : (
        <BucketGroupedErrors projects={projects} />
      )}
    </div>
  )
}

// The Activity ⇄ Errors view toggle shown on both the Activity detail page and the Errors page. Two
// chips linking to /admin/activity and /admin/errors, preserving the current query string (start/end/
// hour/ext) so switching views keeps the same time range and filters. The active view is highlighted.
export function ActivityErrorsViewToggle({ view }: { view: 'activity' | 'errors' }) {
  const [searchParams] = useSearchParams()
  const qs = searchParams.toString()
  const suffix = qs ? `?${qs}` : ''
  return (
    <div className="settings-admin__chart-grouping" role="group" aria-label="View">
      <Link
        to={`/admin/activity${suffix}`}
        className={`settings-admin__chip ${view === 'activity' ? 'settings-admin__chip--active' : ''}`}
        style={{ textDecoration: 'none' }}
        aria-current={view === 'activity' ? 'page' : undefined}
        data-testid="view-toggle-activity"
        data-analytics-event="admin-activity-view-activity-toggle"
      >
        Activity
      </Link>
      <Link
        to={`/admin/errors${suffix}`}
        className={`settings-admin__chip ${view === 'errors' ? 'settings-admin__chip--active' : ''}`}
        style={{ textDecoration: 'none' }}
        aria-current={view === 'errors' ? 'page' : undefined}
        data-testid="view-toggle-errors"
        data-analytics-event="admin-activity-view-errors-toggle"
      >
        Errors
      </Link>
    </div>
  )
}

// The activity breakdown for a single day OR a whole period. Each section is a LazyActivitySection:
// its count comes from the summary (rendered immediately), and its rows are fetched from the section's
// own endpoint only when the user expands it — so opening it never pulls every section's data at once.
// By default the range is the single `day`; pass `start`/`end`/`aggregate` to render one combined
// breakdown for a multi-day period (the week view) instead — the sections then fetch the whole range
// aggregated into one bucket. `day` still supplies the section counts (an aggregated summary row).
// The Visits/Signups-by-source breakdown table (Source · Visits · Signups · Conv.), shared by the
// admin page's standalone "Top sources" section and the activity drill-down's per-period breakdown so
// the two read identically. Inline bars scale to the busiest source. Renders an empty note when
// there's no UTM-tagged traffic.
export function UtmSourceTable({ sources }: { sources: UtmSourceStat[] }) {
  const maxVisits = sources.reduce((m, s) => Math.max(m, s.visits), 0)
  if (sources.length === 0) {
    return <p className="settings-section__description" data-testid="utm-empty">No UTM-tagged traffic in this period.</p>
  }
  return (
    <>
      <table className="settings-admin__utm-table" data-testid="utm-table">
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col" className="settings-admin__utm-num">Visits</th>
            <th scope="col" className="settings-admin__utm-num">Signups</th>
            <th scope="col" className="settings-admin__utm-num">Conv.</th>
          </tr>
        </thead>
        <tbody>
          {sources.map(s => (
            <tr key={s.source} data-testid={`utm-row-${s.source}`}>
              <td>
                <span className="settings-admin__utm-source">{s.source}</span>
                <span className="settings-admin__utm-bar" aria-hidden>
                  <span
                    className="settings-admin__utm-bar-fill"
                    style={{ width: maxVisits > 0 ? `${(s.visits / maxVisits) * 100}%` : '0%' }}
                  />
                </span>
              </td>
              <td className="settings-admin__utm-num" data-testid={`utm-visits-${s.source}`}>{s.visits}</td>
              <td className="settings-admin__utm-num" data-testid={`utm-signups-${s.source}`}>{s.signups}</td>
              <td className="settings-admin__utm-num settings-admin__utm-conv">
                {s.visits > 0 ? `${((s.signups / s.visits) * 100).toFixed(1)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

// The Activity drill-down's traffic-source breakdown: the same Visits/Signups-by-UTM-source table the
// main Admin page shows, but scoped to this period. Unlike the collapsible LazyActivitySections below
// it, this is a persistent, always-visible section pinned to the top of the breakdown (before Visits) —
// the "Top sources" view is the drill-down's headline, so it loads its rows on mount rather than
// hiding behind a toggle. Re-fetches when the query scope changes (external-only, hour, date),
// mirroring the other sections.
function TrafficSourcesSection({ start, end, hour, externalOnly, aggregate }: {
  start: string
  end: string
  hour?: number | null
  externalOnly?: boolean
  aggregate?: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<SourcesDayData | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchSectionRange<SourcesDayData>('sources', start, end, hour ?? null, externalOnly, aggregate))
    } catch (e) {
      if ((e as Error).name !== 'AuthError') setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [start, end, hour, externalOnly, aggregate])

  // Always visible: load on mount and refetch whenever the query scope changes.
  useEffect(() => {
    setData(null)
    setError(null)
    void load()
  }, [load])

  return (
    <div className="settings-admin__day-group" data-testid="activity-section-sources">
      <div className="settings-admin__day-group-title">
        <Target size={12} aria-hidden />
        Top sources
      </div>
      {loading && !data
        ? <div className="settings-admin__day-empty">Loading…</div>
        : error
          ? <div className="settings-admin__day-empty">Failed to load: {error}</div>
          : <div className="settings-admin__list"><UtmSourceTable sources={data?.sources ?? []} /></div>}
    </div>
  )
}

export function ActivityDayDetail({ day, hour, externalOnly, start, end, aggregate }: { day: ActivityDaySummary; hour?: number | null; externalOnly?: boolean; start?: string; end?: string; aggregate?: boolean }) {
  const rangeStart = start ?? day.date
  const rangeEnd = end ?? day.date
  return (
    <div className="settings-admin__day-detail">
      <TrafficSourcesSection start={rangeStart} end={rangeEnd} hour={hour} externalOnly={externalOnly} aggregate={aggregate} />

      <LazyActivitySection<VisitsDayData>
        icon={<Eye size={12} aria-hidden />} title="Visits" count={day.visits} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="visits"
        render={d => (
          <div className="settings-admin__list">
            {d.visits.map((v, i) => {
              const utm = [
                { label: 'source', value: v.utmSource },
                { label: 'medium', value: v.utmMedium },
                { label: 'campaign', value: v.utmCampaign },
                { label: 'term', value: v.utmTerm },
                { label: 'content', value: v.utmContent },
              ].filter((t): t is { label: string; value: string } => t.value != null)
              return (
                <div key={`${v.referrer ?? 'direct'}|${i}`} className="settings-admin__item">
                  <div className="settings-admin__visit-main">
                    <span>{v.referrer ?? 'Direct / none'}</span>
                    {utm.length > 0 && (
                      <span className="settings-admin__visit-utm">
                        {utm.map(t => (
                          <span key={t.label} className="settings-admin__visit-utm-tag">{t.label}: {t.value}</span>
                        ))}
                      </span>
                    )}
                  </div>
                  <span className="settings-admin__item-id">{v.count} visit{v.count === 1 ? '' : 's'}</span>
                </div>
              )
            })}
          </div>
        )}
      />

      <LazyActivitySection<EventsDayData>
        icon={<MousePointerClick size={12} aria-hidden />} title="Events" count={day.events} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="events"
        render={d => <DayEventsByKind kinds={d.kinds} />}
      />

      <LazyActivitySection<ActiveUsersDayData>
        icon={<LogIn size={12} aria-hidden />} title="Active users" count={day.activeUsers} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="active-users"
        render={d => (
          <div className="settings-admin__user-projects">
            {d.users.map(u => <ActiveUserRow key={u.userId} user={u} />)}
          </div>
        )}
      />

      <LazyActivitySection<SignupsDayData>
        icon={<UserPlus size={12} aria-hidden />} title="New signups" count={day.signups} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="signups"
        render={d => (
          <div className="settings-admin__list">
            {d.signups.map(u => (
              <div key={u.userId} className="settings-admin__item">
                <UserLink userId={u.userId} label={userLabel(u)} />
                {u.email && u.name && <span className="settings-admin__item-id">{u.name}</span>}
              </div>
            ))}
          </div>
        )}
      />

      <LazyActivitySection<AnonymousDayData>
        icon={<Activity size={12} aria-hidden />} title="Anonymous visitors" count={day.anonVisitors} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="anonymous"
        render={d => (
          <div className="settings-admin__user-projects">
            {d.visitors.map(v => <AnonymousVisitorRow key={v.distinctId} visitor={v} />)}
          </div>
        )}
      />

      <LazyActivitySection<NewProjectsDayData>
        icon={<FolderPlus size={12} aria-hidden />} title="New projects" count={day.newProjects} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="new-projects"
        render={d => (
          <>
            {groupProjectsByOwner(d.newProjects).map(([owner, projects]) => (
              <div key={owner}>
                <div className="settings-admin__day-owner">
                  {projects[0]?.ownerId
                    ? <UserLink userId={projects[0]!.ownerId!} label={owner} />
                    : <><User size={12} aria-hidden /> {owner}</>}
                  {' '}({projects.length})
                </div>
                <div className="settings-admin__user-project-list">
                  {projects.map(p => (
                    <div key={p.id} className="settings-admin__new-project-item">
                      <Link to={`/admin/projects/${p.id}`} className="settings-admin__user-project-name" data-analytics-event="admin-activity-new-project-link">{p.name}</Link>
                      <ProjectStats bugCount={p.bugCount ?? 0} testRunCount={p.testRunCount ?? 0} />
                      <span className="settings-admin__new-project-cell">
                        {p.createdAt && <ProjectDuration createdAt={p.createdAt} finishedAt={p.finishedAt ?? null} inProgress={p.inProgress ?? false} />}
                      </span>
                      <ProjectFirstRrweb seconds={p.firstRrwebSeconds ?? null} />
                      <CompletionEmailIcon projectId={p.id} sentAt={p.completionEmailSentAt ?? null} />
                      <span className="settings-admin__item-time">{p.createdAt ? timeAgo(p.createdAt) : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      />

      <LazyActivitySection<ChangedProjectsDayData>
        icon={<Pencil size={12} aria-hidden />} title="Projects changed" count={day.changedProjects} start={rangeStart} end={rangeEnd} aggregate={aggregate} hour={hour} externalOnly={externalOnly} section="changed-projects"
        render={d => (
          <div className="settings-admin__user-projects">
            {d.changedProjects.map(p => <ChangedProjectRow key={p.id} project={p} />)}
          </div>
        )}
      />
      {/* Errors have their own dedicated page (/admin/errors, reached via the Activity ⇄ Errors toggle);
          the Activity drill-down no longer embeds an Errors section. */}
    </div>
  )
}
